/**
 * Counts Cache Utility
 * Lightweight module-level cache for dashboard counters to avoid re-fetching
 */
import { type Counts } from "./types";

import { fetchBundleData } from '../../api/bundles';
import { NAVIGATION_PURGE_EVENT } from '../../navigation/sessionStore';

export const COUNTS_TTL_MS = 25_000; // 25 seconds TTL (increased from 15)
export const COUNTS_STALE_TTL_MS = 45_000; // 45 seconds stale TTL - use stale while revalidating

type CacheEntry = { at: number; data: Counts };
type InflightEntry = { owner: object; promise: Promise<Counts> };

const ALL_BRANCH_KEY = "all";
const branchKey = (branch?: string | null): string => {
  if (!branch) return ALL_BRANCH_KEY;
  const trimmed = branch.trim().toLowerCase();
  return trimmed ? `branch:${trimmed}` : ALL_BRANCH_KEY;
};

const navCountsCache = new Map<string, CacheEntry>();
const navCountsInflight = new Map<string, InflightEntry>();
let navCountsGeneration = 0;

function purgePrincipalCounts(): void {
  navCountsGeneration += 1;
  navCountsCache.clear();
  navCountsInflight.clear();
}

if (typeof window !== "undefined") {
  window.addEventListener(NAVIGATION_PURGE_EVENT, purgePrincipalCounts);
}

export function getCache(branch?: string | null): CacheEntry | null {
  return navCountsCache.get(branchKey(branch)) ?? null;
}

export function setCache(
  branch: string | null | undefined,
  data: Counts
): void {
  navCountsCache.set(branchKey(branch), { at: Date.now(), data });
}

export function clearCache(branch?: string | null): void {
  if (typeof branch === "undefined") {
    navCountsCache.clear();
    return;
  }
  navCountsCache.delete(branchKey(branch));
}

export function isCacheValid(branch?: string | null): boolean {
  const entry = getCache(branch);
  if (!entry) return false;
  return Date.now() - entry.at < COUNTS_TTL_MS;
}

export function isCacheStale(branch?: string | null): boolean {
  const entry = getCache(branch);
  if (!entry) return true;
  return Date.now() - entry.at >= COUNTS_STALE_TTL_MS;
}

export async function fetchDashboardCounts(
  branch?: string | null,
  bypass?: boolean
): Promise<Counts> {
  const key = branchKey(branch);

  // Return cached data if still fresh (no refetch needed) - unless bypass is true
  const cached = getCache(branch);
  if (!bypass && cached && Date.now() - cached.at < COUNTS_TTL_MS) {
    return cached.data;
  }

  // If bypass is requested, clear the cache first
  if (bypass) {
    clearCache(branch);
  }

  // If there's already a request in flight, wait for it
  if (navCountsInflight.has(key)) {
    return navCountsInflight.get(key)!.promise;
  }

  const generation = navCountsGeneration;
  const owner = {};
  const request = (async () => {
    try {
      const qs = new URLSearchParams();
      if (branch) qs.set("stats_branch", branch);
      qs.set("page_size", "1");
      qs.set("include", "dashboard");
      if (bypass) qs.set("_r", String(Date.now()));
      const endpoint = qs.toString()
        ? `/reception/api/bundle/home?${qs.toString()}`
        : "/reception/api/bundle/home";
      const payload = await fetchBundleData<{ dashboard?: Partial<Counts> }>(endpoint, {
        cache: "no-store",
        autoLogout: false,
      });
      const d = payload?.dashboard ?? {};
      const data: Counts = {
        pending: d.pending,
        waiting: d.waiting,
        ready: d.ready,
        finished: d.finished,
        refunded: d.refunded,
        didnt_complete: d.didnt_complete,
        groups: d.groups,
        inactive: d.inactive,
        requests: d.requests,
        course_interests: d.course_interests,
        flags: d.flags || {},
        pending_placement_payments: d.pending_placement_payments,
        pending_refunds: d.pending_refunds,
        pending_sales_invoices: d.pending_sales_invoices,
        pending_sales_consult_queue: d.pending_sales_consult_queue,
        employees_total: d.employees_total,
        sales_leads: d.sales_leads,
      };
      if (
        generation === navCountsGeneration
        && navCountsInflight.get(key)?.owner === owner
      ) {
        setCache(branch, data);
      }
      return data;
    } finally {
      if (navCountsInflight.get(key)?.owner === owner) {
        navCountsInflight.delete(key);
      }
    }
  })();

  navCountsInflight.set(key, { owner, promise: request });

  return request;
}


